import { Component, DestroyRef, ElementRef, ViewChild, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatTimestamp, WAT_TIME_ZONE } from '#models';
import { NetworkService } from '#services';

type NetworkIframeMessage =
  | {
      type: 'ontology-network-data';
      payload: unknown;
      focus?: string | null;
      focusCodes?: string[];
      uiMode?: 'compact' | 'full' | 'overlay';
    }
  | { type: 'ontology-network-unavailable'; uiMode?: 'compact' | 'full' | 'overlay' };

@Component({
  selector: 'app-network-preview',
  imports: [],
  templateUrl: './network-preview.component.html',
  styleUrl: './network-preview.component.scss',
})
export class NetworkPreviewComponent {
  private _networkService = inject(NetworkService);
  private _destroyRef = inject(DestroyRef);

  asset = input<string | null | undefined>(null);
  assetCodes = input<string[] | null | undefined>(null);
  fillHeight = input<boolean>(false);
  inlineUiMode = input<'compact' | 'full'>('compact');
  expandable = input<boolean>(true);
  createdDateLoaded = output<string | null>();

  @ViewChild('networkPreviewFrame') private _networkPreviewFrame?: ElementRef<HTMLIFrameElement>;
  @ViewChild('networkPreviewOverlayFrame') private _networkPreviewOverlayFrame?: ElementRef<HTMLIFrameElement>;

  private _inlineFrameLoaded = signal<boolean>(false);
  private _overlayFrameLoaded = signal<boolean>(false);
  private _pendingMessage = signal<NetworkIframeMessage | null>(null);
  private _expanded = signal<boolean>(false);
  private _createdDate = signal<string | null>(null);

  isExpanded = this._expanded.asReadonly();

  constructor() {
    effect(() => {
      const focusCodes = this.getEffectiveAssetCodes();
      const focus = focusCodes[0] ?? this.asset() ?? null;
      const message = this._pendingMessage();
      if (message?.type !== 'ontology-network-data') {
        return;
      }

      const sameFocus = (message.focus ?? null) === focus;
      const sameCodes = JSON.stringify(message.focusCodes ?? []) === JSON.stringify(focusCodes);
      if (sameFocus && sameCodes) {
        return;
      }

      this._pendingMessage.set({ ...message, focus, focusCodes });
      this._postMessageToFrames();
    });

    this._loadNetworkMap();

    window.addEventListener('message', this._onWindowMessage);
    this._destroyRef.onDestroy(() => window.removeEventListener('message', this._onWindowMessage));
  }

  onInlineFrameLoad(): void {
    this._inlineFrameLoaded.set(true);
    this._postMessageToFrames();
  }

  onOverlayFrameLoad(): void {
    this._overlayFrameLoaded.set(true);
    this._postMessageToFrames();
  }

  openOverlay(): void {
    this._expanded.set(true);
    // Re-send after overlay mount so focus and zoom are applied to the large view.
    setTimeout(() => this._postMessageToFrames(), 80);
  }

  closeOverlay(): void {
    this._expanded.set(false);
  }

  // Close message posted by the ontology iframe's in-toolbar close button (overlay mode).
  private _onWindowMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) {
      return;
    }
    if ((event.data as { type?: string } | null)?.type === 'ontology-close') {
      this.closeOverlay();
    }
  };

  getTimestampLabel(): string | null {
    return this._formatCreatedDate();
  }

  getOverlaySubtitle(): string {
    const parts: string[] = [];
    const timestamp = this._formatCreatedDate();
    if (timestamp) {
      parts.push(`Snapshot ${timestamp}`);
    }
    const focus = this.getEffectiveAssetCodes()[0];
    if (focus) {
      parts.push(`focused on ${focus.toUpperCase()}`);
    }
    return parts.join(' \u00b7 ');
  }

  private _formatCreatedDate(): string | null {
    const raw = this._createdDate();
    if (!raw) {
      return null;
    }
    const parsed = Date.parse(raw);
    if (Number.isNaN(parsed)) {
      return raw;
    }
    return formatTimestamp(parsed, { timeZone: WAT_TIME_ZONE });
  }

  private _loadNetworkMap(): void {
    this._networkService
      .getOkanNetworkDiagram()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (payload) => {
          if (payload) {
            const focusCodes = this.getEffectiveAssetCodes();
            this._pendingMessage.set({
              type: 'ontology-network-data',
              payload,
              focus: focusCodes[0] ?? this.asset() ?? null,
              focusCodes,
            });
            this.createdDateLoaded.emit(payload.createdDate ?? null);
            this._createdDate.set(payload.createdDate ?? null);
          } else {
            this._pendingMessage.set({ type: 'ontology-network-unavailable' });
            this.createdDateLoaded.emit(null);
            this._createdDate.set(null);
          }
          this._postMessageToFrames();
        },
        error: () => {
          this._pendingMessage.set({ type: 'ontology-network-unavailable' });
          this.createdDateLoaded.emit(null);
          this._createdDate.set(null);
          this._postMessageToFrames();
        },
      });
  }

  private _postMessageToFrames(): void {
    const message = this._pendingMessage();
    if (!message) {
      return;
    }

    if (this._inlineFrameLoaded()) {
      const inlineWindow = this._networkPreviewFrame?.nativeElement.contentWindow;
      if (inlineWindow) {
        inlineWindow.postMessage(this._withUiMode(message, this.inlineUiMode()), window.location.origin);
      }
    }

    if (this._expanded() && this._overlayFrameLoaded()) {
      const overlayWindow = this._networkPreviewOverlayFrame?.nativeElement.contentWindow;
      if (overlayWindow) {
        overlayWindow.postMessage(this._withUiMode(message, 'overlay'), window.location.origin);
      }
    }
  }

  private _withUiMode(
    message: NetworkIframeMessage,
    uiMode: 'compact' | 'full' | 'overlay'
  ): NetworkIframeMessage {
    return { ...message, uiMode };
  }

  private getEffectiveAssetCodes(): string[] {
    const provided = this.assetCodes() ?? [];
    const cleanedProvided = provided.map((code) => String(code ?? '').trim()).filter((code) => code.length > 0);
    if (cleanedProvided.length > 0) {
      return Array.from(new Set(cleanedProvided.map((code) => code.toLowerCase())));
    }

    const single = String(this.asset() ?? '').trim();
    return single ? [single.toLowerCase()] : [];
  }
}
